var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Skeleton, Stack, Typography, styled, useTheme } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PlanType, PlanTypeCategory } from '../../enum';
import { registerRumStaticText } from '../../observability/allowlist';
import { formatDateToLocaleUppercase } from '../../util';
import { Autocomplete, LineBarChart, ScenarioLegendItem, StyledDatePicker } from '../common';
import { LINE_BUTTONS, lineBarLayout } from '../common/chart/config';
import { scenarioHoverTemplate } from '../common/chart/plotlyText';
import { DISABLED_LEGEND_COLOR, StyledChartContainer, StyledHeaderRow, StyledLegendContainer, StyledTitleChart, } from '../common/chart/styles';
import { QueueType } from '../multiskill/types';
import { useReportAPI } from '../report/selectors';
import { SEAT_EXCEEDED_COLOR } from '../seatCapacity';
import { useIsCompletedSimulation } from '../toastify/selector';
import { PLAN_COLORS } from '../wizard/constants';
import { MULTI_QUEUE_FALLBACK_CHART_METRIC, SINGLE_QUEUE_ONLY_CHART_METRICS } from './constants';
import { ALL_DAILY_FILTER_VALUE, normalizeDailyChartMetric, useDailyContext } from './context';
import { useDailySeatExceeded } from './seatExceeded';
import { isDailyQueueData, useBaselineQueues, useDailyAPI, useDailyChartAPI, useDaysOfWeek, useReportTimelineOptions, } from './selectors';
var StyledTypeControlsText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        fontSize: theme.typography.pxToRem(16),
        width: 'stretch',
    });
});
var StyledChartControls = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    });
});
var StyledControlGroup = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        gap: theme.spacing(4),
        flexDirection: 'row',
        alignItems: 'center',
    });
});
var CHART_METRIC_OPTIONS = [
    { id: 'workVolume', name: 'Forecasted Demand' },
    { id: 'productionFte', name: 'Production FTE' },
    { id: 'requiredFte', name: 'Required (Paid) FTE' },
    { id: 'seatsRequired', name: 'Seats Required' },
    { id: 'serviceLevel', name: 'Service Level' },
    { id: 'asa', name: 'ASA' },
    { id: 'occupancy', name: 'Occupancy' },
    { id: 'abandonmentRate', name: 'Abandonment Rate' },
];
var PROD_HOURS_CHART_METRIC_OPTIONS = [
    { id: 'productiveHours', name: 'Productive Hours' },
    { id: 'productionHours', name: 'Production Hours' },
    { id: 'productionFte', name: 'Production FTE' },
    { id: 'requiredFte', name: 'Required FTE' },
    { id: 'seatsRequired', name: 'Seats Required' },
];
registerRumStaticText(__spreadArray(__spreadArray([], CHART_METRIC_OPTIONS, true), PROD_HOURS_CHART_METRIC_OPTIONS, true).map(function (_a) {
    var name = _a.name;
    return name;
}));
var PERCENTAGE_CHART_METRICS = new Set(['serviceLevel', 'occupancy', 'abandonmentRate']);
var INTEGER_CHART_METRICS = new Set([
    'seatsRequired',
    'workVolume',
    'asa',
    'productiveHours',
    'productionHours',
]);
var DAILY_CHART_VISIBLE_POINTS = 10;
var INTERVAL_CHART_VISIBLE_POINTS = 10;
var CHART_SKELETON_TICKS = [0, 1, 2, 3, 4, 5, 6];
var CHART_SKELETON_LEGENDS = [0, 1, 2];
var CHART_DATE_FORMAT = 'YYYY-MM-DD';
var API_CHART_DATE_FORMAT = 'YY-MMM-DD';
var API_WEEKDAYS = moment.localeData('en').weekdays();
var formatChartLabel = function (label, language, dateFormat) {
    var parts = label.split('<br>');
    var datePart = parts.find(function (part) { return moment.utc(part, API_CHART_DATE_FORMAT, 'en', true).isValid(); });
    var date = datePart ? moment.utc(datePart, API_CHART_DATE_FORMAT, 'en', true) : null;
    return parts
        .map(function (part) {
        var parsed = moment.utc(part, API_CHART_DATE_FORMAT, 'en', true);
        if (parsed.isValid())
            return formatDateToLocaleUppercase(parsed.locale(language).format(dateFormat));
        var parsedTime = moment.utc(part, ['HH:mm', 'hh:mmA'], true);
        if (parsedTime.isValid())
            return parsedTime.format('hh:mmA');
        var weekdayIndex = API_WEEKDAYS.findIndex(function (weekday) { return weekday.toLowerCase() === part.toLowerCase(); });
        if (weekdayIndex >= 0) {
            return formatDateToLocaleUppercase((date !== null && date !== void 0 ? date : moment().day(weekdayIndex)).locale(language).format('dddd'));
        }
        return part;
    })
        .join('<br>');
};
var ScenarioLegend = function (_a) {
    var scenarios = _a.scenarios, visibility = _a.visibility, onToggle = _a.onToggle, getScenarioLabel = _a.getScenarioLabel;
    var theme = useTheme();
    var reportData = useReportAPI()[0].data;
    return (_jsx(StyledLegendContainer, { children: scenarios.map(function (_a) {
            var _b, _c;
            var scenarioName = _a.scenarioName, colorIndex = _a.colorIndex, isMultiskill = _a.isMultiskill;
            var color = ((_b = PLAN_COLORS[colorIndex]) === null || _b === void 0 ? void 0 : _b[0]) || '#1976d2';
            var isChecked = visibility[scenarioName] !== false;
            var lineColor = isChecked ? color : DISABLED_LEGEND_COLOR;
            // The chart payload carries no plan id; colorIndex is unique per scenario (see useScenarioPlanIdByColorIndex).
            // The baseline's label is static text, so it gets no id.
            var plan = (_c = reportData === null || reportData === void 0 ? void 0 : reportData.scenarios) === null || _c === void 0 ? void 0 : _c.find(function (scenario) { return scenario.colorIndex === colorIndex; });
            return (_jsx(ScenarioLegendItem, { scenarioName: scenarioName, scenarioId: (plan === null || plan === void 0 ? void 0 : plan.isBaseline) ? undefined : plan === null || plan === void 0 ? void 0 : plan.id, isMultiskill: !!isMultiskill, isChecked: isChecked, color: color, lineColor: lineColor, label: getScenarioLabel(scenarioName), textColor: isChecked ? color : theme.palette.text.secondary, onToggle: onToggle, theme: theme }, scenarioName));
        }) }));
};
export var DailyChart = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var tableReady = _a.tableReady;
    var _l = useTranslation(['report', 'translation']), i18n = _l.i18n, t = _l.t;
    var _m = useDailyContext(), state = _m[0], setState = _m[1];
    var reportData = useReportAPI()[0].data;
    var timelines = useReportTimelineOptions();
    var _o = useDailyChartAPI(), chartAPIState = _o[0], fetchChartData = _o[1];
    var basePlanId = useParams().basePlanId;
    var isCompletedSimulation = useIsCompletedSimulation(basePlanId);
    var isProdHoursPlan = (reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) === PlanTypeCategory.ProdHours;
    var isInterval = (state === null || state === void 0 ? void 0 : state.viewType) === 'interval';
    var canSelectPeriodRange = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Weekly;
    var chartMetricOptions = isProdHoursPlan ? PROD_HOURS_CHART_METRIC_OPTIONS : CHART_METRIC_OPTIONS;
    var baselineQueues = useBaselineQueues(state === null || state === void 0 ? void 0 : state.uniqueId);
    var isMultiskillQueue = baselineQueues.some(function (queue) { return queue.id === (state === null || state === void 0 ? void 0 : state.chartQueueId) && queue.queueType === QueueType.Multiskill; });
    var isMultiQueue = ((_c = (_b = state === null || state === void 0 ? void 0 : state.chartQueueIds) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 1 || isMultiskillQueue;
    var isMetricDisabled = function (metric) {
        return !isProdHoursPlan && isMultiQueue && SINGLE_QUEUE_ONLY_CHART_METRICS.has(metric);
    };
    var normalizedMetric = normalizeDailyChartMetric(state === null || state === void 0 ? void 0 : state.chartMetric, isProdHoursPlan);
    var shouldFallbackMetric = isMetricDisabled(normalizedMetric);
    var selectedMetric = shouldFallbackMetric ? MULTI_QUEUE_FALLBACK_CHART_METRIC : normalizedMetric;
    var isPercentageMetric = PERCENTAGE_CHART_METRICS.has(selectedMetric);
    var control = useForm().control;
    var chartSubLobRequestKey = (state === null || state === void 0 ? void 0 : state.chartLobId) === ALL_DAILY_FILTER_VALUE ||
        (typeof (state === null || state === void 0 ? void 0 : state.chartLobId) === 'string' && state.chartLobId.startsWith('lob:'))
        ? undefined
        : state === null || state === void 0 ? void 0 : state.chartSubLobId;
    var chartRequestKey = [
        basePlanId,
        ((_d = state === null || state === void 0 ? void 0 : state.uniqueId) === null || _d === void 0 ? void 0 : _d.includes('-')) ? 'multi' : 'single',
        state === null || state === void 0 ? void 0 : state.viewType,
        state === null || state === void 0 ? void 0 : state.chartLobId,
        chartSubLobRequestKey,
        state === null || state === void 0 ? void 0 : state.chartQueueId,
        state === null || state === void 0 ? void 0 : state.month,
        state === null || state === void 0 ? void 0 : state.startMonth,
        state === null || state === void 0 ? void 0 : state.endMonth,
        state === null || state === void 0 ? void 0 : state.activeDate,
        reportData === null || reportData === void 0 ? void 0 : reportData.planType,
        isCompletedSimulation,
    ].join('|');
    var lastChartRequestKey = useRef(undefined);
    var _p = useDailySeatExceeded(), isDayExceeded = _p.isDayExceeded, isIntervalExceeded = _p.isIntervalExceeded;
    var daysOfWeek = useDaysOfWeek();
    var dailyDetailAPIState = useDailyAPI()[0];
    var dailyDetailData = dailyDetailAPIState.data;
    var dailyTimes = useMemo(function () { var _a; return (isDailyQueueData(dailyDetailData) ? (_a = dailyDetailData.times) !== null && _a !== void 0 ? _a : [] : []); }, [dailyDetailData]);
    var activeDayIndex = useMemo(function () {
        var _a, _b;
        var activeDate = (_a = state === null || state === void 0 ? void 0 : state.activeDate) !== null && _a !== void 0 ? _a : (_b = daysOfWeek[0]) === null || _b === void 0 ? void 0 : _b.id;
        if (!activeDate)
            return 0;
        var idx = daysOfWeek.findIndex(function (d) { return d.id === activeDate; });
        return idx >= 0 ? idx : moment.utc(activeDate).isoWeekday() - 1;
    }, [state === null || state === void 0 ? void 0 : state.activeDate, daysOfWeek]);
    var _q = useState({}), scenarioVisibility = _q[0], setScenarioVisibility = _q[1];
    var handleToggleScenario = useCallback(function (name) {
        setScenarioVisibility(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[name] = prev[name] === undefined ? false : !prev[name], _a)));
        });
    }, []);
    // The table intentionally owns the first CPU-heavy request. Fetch only after it
    // settles and after the chart state is valid, so aborted requests do not keep
    // consuming report-api CPU in the background.
    useEffect(function () {
        var _a;
        var hasValidDate = !isInterval || !!(state === null || state === void 0 ? void 0 : state.activeDate);
        if (tableReady && hasValidDate && ((_a = state === null || state === void 0 ? void 0 : state.chartQueueIds) === null || _a === void 0 ? void 0 : _a.length) && lastChartRequestKey.current !== chartRequestKey) {
            lastChartRequestKey.current = chartRequestKey;
            fetchChartData();
        }
    }, [chartRequestKey, fetchChartData, isInterval, state === null || state === void 0 ? void 0 : state.activeDate, state === null || state === void 0 ? void 0 : state.chartQueueIds, tableReady]);
    var planDateRange = useMemo(function () {
        if (!(reportData === null || reportData === void 0 ? void 0 : reportData.startDate) || !(reportData === null || reportData === void 0 ? void 0 : reportData.periodLength) || !(reportData === null || reportData === void 0 ? void 0 : reportData.planType)) {
            return null;
        }
        var start = moment.utc(reportData.startDate).startOf('day');
        var end = start
            .clone()
            .add(reportData.periodLength, reportData.planType === PlanType.Weekly ? 'weeks' : 'months')
            .subtract(1, 'day');
        return { start: start, end: end };
    }, [reportData === null || reportData === void 0 ? void 0 : reportData.periodLength, reportData === null || reportData === void 0 ? void 0 : reportData.planType, reportData === null || reportData === void 0 ? void 0 : reportData.startDate]);
    useEffect(function () {
        var _a, _b;
        if ((state === null || state === void 0 ? void 0 : state.viewType) !== 'interval' || !planDateRange) {
            return;
        }
        var selectedMonth = Math.max(0, Math.min((_a = state.month) !== null && _a !== void 0 ? _a : 0, ((_b = reportData === null || reportData === void 0 ? void 0 : reportData.periodLength) !== null && _b !== void 0 ? _b : 1) - 1));
        var periodUnit = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Weekly ? 'weeks' : 'months';
        var selectedPeriodStart = planDateRange.start.clone().add(selectedMonth, periodUnit);
        var selectedPeriodEnd = selectedPeriodStart.clone().add(1, periodUnit);
        var activeDate = (state === null || state === void 0 ? void 0 : state.activeDate) ? moment.utc(state.activeDate) : null;
        var isActiveDateInSelectedPeriod = (activeDate === null || activeDate === void 0 ? void 0 : activeDate.isValid()) &&
            !activeDate.isBefore(selectedPeriodStart) &&
            activeDate.isBefore(selectedPeriodEnd) &&
            !activeDate.isAfter(planDateRange.end);
        if (!isActiveDateInSelectedPeriod) {
            setState(function (curr) {
                var _a, _b;
                return curr
                    ? __assign(__assign({}, curr), { activeDate: selectedPeriodStart.format(CHART_DATE_FORMAT), month: selectedMonth, startMonth: (_a = curr.startMonth) !== null && _a !== void 0 ? _a : selectedMonth, endMonth: (_b = curr.endMonth) !== null && _b !== void 0 ? _b : selectedMonth }) : curr;
            });
        }
    }, [planDateRange, reportData === null || reportData === void 0 ? void 0 : reportData.periodLength, reportData === null || reportData === void 0 ? void 0 : reportData.planType, setState, state]);
    var handleMetricChange = function (metricKey) {
        setState(function (curr) { return (curr ? __assign(__assign({}, curr), { chartMetric: metricKey }) : curr); });
    };
    var getPeriodIndexByDate = useCallback(function (date) {
        var _a;
        if (!planDateRange || !(reportData === null || reportData === void 0 ? void 0 : reportData.planType)) {
            return (_a = state === null || state === void 0 ? void 0 : state.month) !== null && _a !== void 0 ? _a : 0;
        }
        var unit = reportData.planType === PlanType.Weekly ? 'weeks' : 'months';
        return Math.max(0, Math.min(reportData.periodLength - 1, date.clone().startOf('day').diff(planDateRange.start, unit)));
    }, [planDateRange, reportData === null || reportData === void 0 ? void 0 : reportData.periodLength, reportData === null || reportData === void 0 ? void 0 : reportData.planType, state === null || state === void 0 ? void 0 : state.month]);
    var handleActiveDateChange = function (date) {
        if (!(date === null || date === void 0 ? void 0 : date.isValid())) {
            return;
        }
        var newMonth = getPeriodIndexByDate(date);
        setState(function (curr) {
            return curr
                ? __assign(__assign({}, curr), { activeDate: date.format(CHART_DATE_FORMAT), month: newMonth, startMonth: newMonth, endMonth: newMonth }) : curr;
        });
    };
    var selectedStartMonth = (_h = (_f = (_e = state === null || state === void 0 ? void 0 : state.startMonth) !== null && _e !== void 0 ? _e : state === null || state === void 0 ? void 0 : state.month) !== null && _f !== void 0 ? _f : (_g = timelines[0]) === null || _g === void 0 ? void 0 : _g.id) !== null && _h !== void 0 ? _h : 0;
    var selectedEndMonth = Math.max((_j = state === null || state === void 0 ? void 0 : state.endMonth) !== null && _j !== void 0 ? _j : selectedStartMonth, selectedStartMonth);
    var endTimelineOptions = timelines.filter(function (timeline) { return timeline.id >= selectedStartMonth; });
    var handleStartMonthChange = function (value) {
        setState(function (curr) {
            var _a;
            return curr
                ? __assign(__assign({}, curr), { month: value, startMonth: value, endMonth: canSelectPeriodRange ? Math.max((_a = curr.endMonth) !== null && _a !== void 0 ? _a : value, value) : value }) : curr;
        });
    };
    var handleEndMonthChange = function (value) {
        setState(function (curr) {
            var _a, _b;
            return curr
                ? __assign(__assign({}, curr), { endMonth: canSelectPeriodRange ? Math.max(value, (_b = (_a = curr.startMonth) !== null && _a !== void 0 ? _a : curr.month) !== null && _b !== void 0 ? _b : value) : value }) : curr;
        });
    };
    var chartData = chartAPIState.data;
    var chartScenarios = useMemo(function () { var _a; return (_a = chartData === null || chartData === void 0 ? void 0 : chartData.metrics[selectedMetric]) !== null && _a !== void 0 ? _a : []; }, [chartData, selectedMetric]);
    var getScenarioLabel = useCallback(function (scenarioName) { return (scenarioName === 'Baseline' ? t('translation:baseline') : scenarioName); }, [t]);
    useEffect(function () {
        if (shouldFallbackMetric) {
            setState(function (curr) { return (curr ? __assign(__assign({}, curr), { chartMetric: MULTI_QUEUE_FALLBACK_CHART_METRIC }) : curr); });
        }
    }, [shouldFallbackMetric, setState]);
    var chartProperties = useMemo(function () {
        if (!chartData)
            return null;
        var config = { modeBarButtons: LINE_BUTTONS };
        var layout = structuredClone(lineBarLayout);
        var labels = chartData.labels || [];
        var displayLabels = labels.map(function (label) {
            return formatChartLabel(label, i18n.language, t('translation:dateFormat.dayMonthYearFormat'));
        });
        var count = labels.length;
        var visiblePointCount = (state === null || state === void 0 ? void 0 : state.viewType) === 'interval' ? INTERVAL_CHART_VISIBLE_POINTS : DAILY_CHART_VISIBLE_POINTS;
        var autoRange = count <= visiblePointCount;
        var range = [-0.5, visiblePointCount - 0.5];
        var isIntegerMetric = INTEGER_CHART_METRICS.has(selectedMetric);
        var yValueFormat = (function () {
            if (isPercentageMetric) {
                return '%{y:.1%}';
            }
            if (isIntegerMetric) {
                return '%{y:,.0f}';
            }
            return '%{y:,.1f}';
        })();
        var plotData = chartScenarios
            .filter(function (s) { return scenarioVisibility[s.scenarioName] !== false; })
            .map(function (scenario) {
            var _a;
            var color = ((_a = PLAN_COLORS[scenario.colorIndex]) === null || _a === void 0 ? void 0 : _a[0]) || '#1976d2';
            var scenarioLabel = getScenarioLabel(scenario.scenarioName);
            var isSeatsRequiredMetric = selectedMetric === 'seatsRequired';
            // Soft red fill on markers for exceeded day/interval columns so highlights remain
            // visible even when chart label count doesn't match detail-window length for shapes.
            var markerColors = isSeatsRequiredMetric &&
                ((state === null || state === void 0 ? void 0 : state.viewType) === 'interval'
                    ? labels.map(function (_, i) {
                        return dailyTimes[i] != null && isIntervalExceeded(activeDayIndex, dailyTimes[i]) ? SEAT_EXCEEDED_COLOR : color;
                    })
                    : labels.map(function (_, i) { return (isDayExceeded(i) ? SEAT_EXCEEDED_COLOR : color); }));
            return {
                type: 'scatter',
                mode: 'lines+markers',
                name: scenarioLabel,
                x: displayLabels,
                y: scenario.values,
                line: {
                    color: color,
                    width: 3,
                    shape: 'spline',
                    dash: scenario.isMultiskill ? 'dash' : undefined,
                },
                marker: {
                    color: markerColors || color,
                    size: 8,
                    line: markerColors
                        ? {
                            color: labels.map(function (_, i) {
                                var exceeded = (state === null || state === void 0 ? void 0 : state.viewType) === 'interval'
                                    ? dailyTimes[i] != null && isIntervalExceeded(activeDayIndex, dailyTimes[i])
                                    : isDayExceeded(i);
                                return exceeded ? '#c9211e' : color;
                            }),
                            width: 1,
                        }
                        : undefined,
                },
                connectgaps: true,
                hovertemplate: scenarioHoverTemplate(scenarioLabel, yValueFormat),
                hoverlabel: {
                    bordercolor: '#fff',
                },
            };
        });
        if (!plotData.length && labels.length) {
            plotData.push({
                type: 'scatter',
                mode: 'lines+markers',
                x: displayLabels,
                y: labels.map(function () { return null; }),
            });
        }
        var tickFormat;
        if (isPercentageMetric) {
            tickFormat = '.1%';
        }
        else if (isIntegerMetric) {
            tickFormat = ',.0f';
        }
        layout.yaxis = __assign(__assign({}, layout.yaxis), { showgrid: true, gridcolor: '#D7D7D7', tickformat: tickFormat, hoverformat: tickFormat });
        layout.xaxis = __assign(__assign({}, layout.xaxis), { autorange: autoRange, fixedrange: !autoRange, automargin: true, showgrid: true, gridwidth: 100, range: range, rangeslider: {
                visible: !autoRange,
                thickness: 0.04,
                bgcolor: '#f1f1f1',
                range: [-0.5, count - 0.5],
            } });
        // Column band when chart labels align with detail data; markers still show when they don't.
        if (selectedMetric === 'seatsRequired') {
            var isExceededAt_1 = (state === null || state === void 0 ? void 0 : state.viewType) === 'interval'
                ? function (i) {
                    return count === dailyTimes.length
                        ? isIntervalExceeded(activeDayIndex, dailyTimes[i])
                        : dailyTimes[i] != null && isIntervalExceeded(activeDayIndex, dailyTimes[i]);
                }
                : function (i) { return isDayExceeded(i); };
            layout.shapes = labels.reduce(function (shapes, _label, i) {
                if (!isExceededAt_1(i))
                    return shapes;
                shapes.push({
                    type: 'rect',
                    xref: 'x',
                    x0: i - 0.5,
                    x1: i + 0.5,
                    yref: 'paper',
                    y0: 0,
                    y1: 1,
                    fillcolor: SEAT_EXCEEDED_COLOR,
                    line: { width: 0 },
                    layer: 'below',
                });
                return shapes;
            }, []);
        }
        return {
            data: plotData,
            layout: layout,
            config: config,
            xAxisConfig: {
                rangeFixed: range,
                maxRange: count - 1,
                viewRange: autoRange ? count : visiblePointCount,
            },
        };
    }, [
        activeDayIndex,
        chartData,
        chartScenarios,
        dailyTimes,
        getScenarioLabel,
        i18n.language,
        isDayExceeded,
        isIntervalExceeded,
        isPercentageMetric,
        scenarioVisibility,
        selectedMetric,
        state === null || state === void 0 ? void 0 : state.viewType,
        t,
    ]);
    var metricTitle = t("daily.chart.metrics.".concat(selectedMetric));
    var isChartLoading = !!((_k = state === null || state === void 0 ? void 0 : state.chartQueueIds) === null || _k === void 0 ? void 0 : _k.length) && (!tableReady || (isInterval && !state.activeDate) || !!chartAPIState.requesting);
    if (!chartData && !isChartLoading)
        return null;
    return (_jsxs(StyledChartContainer, { children: [_jsxs(StyledHeaderRow, { children: [_jsx(StyledTitleChart, { children: metricTitle }), _jsxs(StyledChartControls, { children: [isInterval ? (_jsx(StyledControlGroup, { children: _jsx(StyledDatePicker, { name: "activeDate", control: control, minDate: planDateRange === null || planDateRange === void 0 ? void 0 : planDateRange.start, maxDate: planDateRange === null || planDateRange === void 0 ? void 0 : planDateRange.end, value: (state === null || state === void 0 ? void 0 : state.activeDate) ? moment.utc(state.activeDate) : null, format: t('translation:dateFormat.short'), onChange: handleActiveDateChange }) })) : (_jsxs(StyledControlGroup, { children: [_jsx(Autocomplete, { disablePortal: true, sx: { minWidth: '14rem' }, options: timelines, value: selectedStartMonth, getOptionValue: function (o) { return o.id; }, onChange: handleStartMonthChange }), canSelectPeriodRange && (_jsxs(_Fragment, { children: [_jsx(StyledTypeControlsText, { children: t('actual.to', { ns: 'report' }) }), _jsx(Autocomplete, { disablePortal: true, sx: { minWidth: '14rem' }, options: endTimelineOptions, value: selectedEndMonth, getOptionValue: function (o) { return o.id; }, onChange: handleEndMonthChange })] }))] })), _jsxs(StyledControlGroup, { children: [_jsx(StyledTypeControlsText, __assign({ variant: "body2" }, { children: t('daily.selectMetric') })), _jsx(Autocomplete, { disablePortal: true, sx: { minWidth: '14rem' }, options: chartMetricOptions, value: selectedMetric, getOptionValue: function (o) { return o.id; }, getOptionLabel: function (o) { return t("daily.chart.metrics.".concat(o.id)); }, getOptionDisabled: function (o) { return isMetricDisabled(o.id); }, onChange: handleMetricChange })] })] })] }), _jsxs(Box, __assign({ sx: { position: 'relative', minHeight: '30rem' }, "aria-label": metricTitle, "aria-busy": isChartLoading }, { children: [chartProperties && (_jsx(Box, __assign({ sx: { height: '30rem' } }, { children: _jsx(LineBarChart, __assign({}, chartProperties)) }))), !isChartLoading && chartData && (_jsx(ScenarioLegend, { scenarios: chartScenarios, visibility: scenarioVisibility, onToggle: handleToggleScenario, getScenarioLabel: getScenarioLabel })), isChartLoading && (_jsxs(Stack, __assign({ "data-testid": "chart-skeleton", sx: __assign({ height: '30rem', p: 3, gap: 2 }, (chartData && { position: 'absolute', inset: 0, zIndex: 1, bgcolor: 'background.paper' })) }, { children: [_jsx(Skeleton, { variant: "rounded", animation: "wave", sx: { flex: 1, transform: 'none' } }), _jsx(Stack, __assign({ direction: "row", justifyContent: "space-between" }, { children: CHART_SKELETON_TICKS.map(function (tick) { return (_jsx(Skeleton, { variant: "text", animation: "wave", width: "8%" }, tick)); }) })), _jsx(Stack, __assign({ direction: "row", justifyContent: "center", gap: 3 }, { children: CHART_SKELETON_LEGENDS.map(function (legend) { return (_jsx(Skeleton, { variant: "rounded", animation: "wave", width: "8rem", height: "1.5rem" }, legend)); }) }))] })))] }))] }));
};
